import React from 'react';
import classNames from 'classnames/bind';

import AIModel from 'types/ai_model';
import { Deployment } from 'types/deployment';
import { Camera } from 'types/camera';
import { Gateway } from 'types/gateway';
import { Stream } from 'types/stream';
import { TrackableItem, TrackableList } from 'types/trackables';
import { Tag } from 'types/tag';

import { TaggableEntityObject } from 'hooks/useEntities';
import { useAddEntityTags } from 'tags/hooks/useAddEntityTag';
import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';
import { useDeleteEntityTags } from 'tags/hooks/useDeleteEntityTags';
import { useHasAccess } from 'hooks/useHasAccess';

import { TagsList } from 'tags/components/TagsList/TagsList';

import { APIObject } from 'services/api';
import { DetailViewHeaderProps } from './DetailViewHeader';
import styles from './DetailViewHeader.module.scss';

const c = classNames.bind(styles);

type DetailViewTagsProps<
  T extends
    | Gateway
    | Camera
    | AIModel
    | Deployment
    | Stream
    | TrackableItem
    | TrackableList,
> = Pick<
  DetailViewHeaderProps<T>,
  'entity' | 'entityType' | 'listQueryKey' | 'singleQueryKey'
>;

export function DetailViewTags<
  T extends
    | Gateway
    | Camera
    | AIModel
    | Deployment
    | Stream
    | TrackableItem
    | TrackableList,
>({
  entity,
  entityType,
  singleQueryKey,
  listQueryKey,
}: DetailViewTagsProps<T>) {
  const [hasAccess] = useHasAccess();

  const { data: currentPlan, isSuccess } = useCurrentPlan();
  const canCreateTags =
    (isSuccess && currentPlan === null) ||
    Boolean(currentPlan?.can_create_tags);

  const { id } = entity;
  const canTag = entity && 'tags' in entity;

  const { mutate: addTags } = useAddEntityTags({
    entity: entityType as keyof APIObject,
    listQueryKey,
    singleQueryKey,
  });
  const { mutate: removeTags } = useDeleteEntityTags({
    entity: entityType as keyof APIObject,
    listQueryKey,
    singleQueryKey,
  });

  const canEditTags = hasAccess('deploy_edit', entityType);

  const handleAdd = React.useCallback(
    (tag: Tag) => {
      addTags({ id, tagIds: [tag.id] });
    },
    [id, addTags]
  );

  const handleRemove = React.useCallback(
    (tagId: Tag['id']) => {
      removeTags({ id, tagIds: [tagId] });
    },
    [id, removeTags]
  );

  if (!canTag || !canCreateTags) {
    return null;
  }

  const { tags } = entity as TaggableEntityObject;

  return (
    <TagsList
      className={c('tags')}
      defaultValue={tags}
      onAdd={handleAdd}
      onRemove={handleRemove}
      readOnly={!canEditTags}
    />
  );
}
